import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Target, Shield, TrendingUp, Users, CheckCircle2, MessageSquare } from 'lucide-react';
import { companyInfo, stats, valores, diferenciadores } from '../data/mockData';

export const Home = () => {
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Hola, me interesa conocer más sobre sus soluciones industriales');
    window.open(`https://wa.me/${companyInfo.contact.whatsapp.replace(/\s/g, '')}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/hero-industrial.jpg" 
            alt="Maquinaria Industrial MAESI"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-800/85 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Soluciones Industriales de{' '}
              <span className="text-blue-300">Precisión y Calidad</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100 leading-relaxed">
              Más de 25 años diseñando y fabricando maquinaria industrial que transforma tu producción
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/equipos"
                className="px-8 py-4 bg-white text-blue-900 font-bold rounded-lg hover:bg-blue-50 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 flex items-center justify-center gap-2 group"
              >
                Ver Equipos
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <button
                onClick={handleWhatsAppClick}
                className="px-8 py-4 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 flex items-center justify-center gap-2"
              >
                <MessageSquare className="w-5 h-5" />
                Contactar por WhatsApp
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.id} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-blue-100 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Compromiso con la{' '}
                <span className="text-blue-600">Excelencia Industrial</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Desde 1998, MAESI ha sido sinónimo de calidad, precisión y confianza en el sector de maquinaria industrial. Nos especializamos en diseñar soluciones personalizadas que optimizan procesos y elevan la productividad.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Nuestra experiencia abarca desde agitadores y llenadoras hasta sistemas completos de transporte y etiquetado, siempre con el más alto estándar de calidad.
              </p>
              <Link
                to="/nosotros"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Conoce Más Sobre Nosotros
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="relative">
              <img 
                src="/images/maesi-personal.jpg" 
                alt="Personal MAESI trabajando"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="flex items-center gap-3">
                  <Award className="w-10 h-10 text-blue-600" />
                  <div>
                    <div className="text-2xl font-bold text-gray-900">25+</div>
                    <div className="text-sm text-gray-600">Años de Experiencia</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Valores Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nuestros <span className="text-blue-600">Valores</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Los principios que guían cada proyecto y solución que entregamos
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {valores.map((valor) => {
              const IconComponent = valor.icon === 'Shield' ? Shield : valor.icon === 'Award' ? Award : Target;
              return (
                <div 
                  key={valor.id}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{valor.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{valor.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Diferenciadores */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              ¿Por Qué Elegir <span className="text-blue-600">MAESI</span>?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ventajas competitivas que nos posicionan como líderes en el sector
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {diferenciadores.map((item) => (
              <div 
                key={item.id}
                className="p-6 border-2 border-blue-100 rounded-xl hover:border-blue-600 hover:bg-blue-50 transition-all duration-300 group"
              >
                <CheckCircle2 className="w-10 h-10 text-blue-600 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-300 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <TrendingUp className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            ¿Listo para Optimizar tu Producción?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto leading-relaxed">
            Contacta con nuestro equipo de expertos y descubre cómo podemos diseñar la solución perfecta para tu empresa
          </p>
          <Link
            to="/contacto"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-900 font-bold rounded-lg hover:bg-blue-50 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
          >
            Solicitar Cotización
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};
